import React, { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import PriceCalendar from './PriceCalendar';
import { shouldShowVenuePricing } from '../../utils/venuePricing';
import {
  calendarHasAnyDiscountedDay,
  getDynamicPricingCoverageForDate,
  selectedCalendarDateHasDiscount
} from '../../utils/venueCalendarPricing';
import venueDashboardApiService from '../../services/venueDashboardApi';
import { getNextAvailableStartTime, getBlockedTimesFromOperatingHoursAndHolidays, mergeOperatingHoursForBookingMode, pickFirstValidEndAfterStart, resolveFullVenueDynamicHourlyRate, buildMergedBlockedStartTimes, buildMergedBlockedEndTimesForStart, isDateFullyUnavailableForBothModes, resolvePerPersonRateForBooking } from '../VenueDashboard/utils/bookingUtils';
import { isPerPersonBookingEnabled, coerceBookingTypeForRules, resolveDefaultBookingType } from '../../utils/bookingPricingMetadata';
import { getPastTimesForDate, getYmdInTimezone } from '../VenueDashboard/utils/dateUtils';
import TimeRangePicker from '../Common/TimeRangePicker';
import SpaceSelect from '../Common/SpaceSelect';
import { InfoCircleIcon } from '../icons';
import { normalizeVenueSpacesForDisplay } from '../../utils/venueSpacesDisplay';

const OffersIcon = () => (
  <img src="/manual-booking/icons/discount-tag.png" alt="" width="18" height="18" />
);

const VenueBookingSidebar = ({
  venue,
  spaces: spacesProp,
  operatingHours = {},
  operatingHoursPerPerson = {},
  holidays = [],
  selectedDate,
  selectedDateCoverage,
  startTime,
  endTime,
  isFlexible,
  dynamicPricing,
  defaultPerPersonPrice,
  dynamicPricingPerPerson = [],
  flatPricing = [],
  bookingRules = null,
  isStartTimeOpen,
  isEndTimeOpen,
  onDateSelect,
  onStartTimeChange,
  onEndTimeChange,
  onFlexibleChange,
  onBookNow,
  toggleStartTime,
  toggleEndTime,
  calculateDuration,
  bookButtonLabel = 'Continue to next step',
  sidebarDataLoading = false
}) => {
  const hidePrice = !shouldShowVenuePricing(venue);
  const [blocks, setBlocks] = useState([]);
  const [loadingBusyTimes, setLoadingBusyTimes] = useState(false);
  const [availabilityPreview, setAvailabilityPreview] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [selectedSpaceId, setSelectedSpaceId] = useState('');
  const perPersonEnabled = isPerPersonBookingEnabled(bookingRules);
  const [bookingType, setBookingType] = useState(() => resolveDefaultBookingType(bookingRules));
  const [tableCount, setTableCount] = useState(20); // number | '' while typing
  const [debouncedTableCount, setDebouncedTableCount] = useState(20);
  /** Calendar month shown in PriceCalendar (for prefetching availability per day). */
  const [, setCalendarVisibleMonth] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  /** YYYY-MM-DD where neither mode has a valid start + min-duration end pair. */
  const [fullyUnavailableDatesYmd, setFullyUnavailableDatesYmd] = useState(() => new Set());
  const venueTimezone = venue?.timezone || venue?.time_zone || 'Europe/London';
  /** Cache fully-unavailable flag per day (keyed below) to avoid refetching when switching months. */
  const dayAvailabilityCacheRef = useRef(new Map());

  const availabilityDayCachePrefix = useMemo(
    () =>
      `${venue?.id}|${selectedSpaceId}|${venueTimezone}|${JSON.stringify(bookingRules)}|${JSON.stringify(holidays)}|${JSON.stringify(operatingHours)}|${JSON.stringify(operatingHoursPerPerson)}`,
    [venue?.id, selectedSpaceId, venueTimezone, bookingRules, holidays, operatingHours, operatingHoursPerPerson]
  );

  useEffect(() => {
    dayAvailabilityCacheRef.current.clear();
  }, [availabilityDayCachePrefix]);

  useEffect(() => {
    setBookingType((prev) => coerceBookingTypeForRules(prev, bookingRules));
  }, [bookingRules, perPersonEnabled]);

  const minHoursForMode = useMemo(() => {
    const isFullVenue = bookingType === 'full-venue';
    return isFullVenue
      ? (bookingRules?.min_hours_exclusive ?? bookingRules?.min_total_hours ?? 2)
      : (bookingRules?.min_hours_per_person ?? 2);
  }, [bookingType, bookingRules]);

  const calendarBookingMode = bookingType === 'full-venue' ? 'exclusive' : 'per_person';
  const pricingRulesForCoverage =
    calendarBookingMode === 'per_person' ? dynamicPricingPerPerson : dynamicPricing;

  const venueHasAnyDiscountedDay = useMemo(
    () =>
      calendarHasAnyDiscountedDay({
        bookingMode: calendarBookingMode,
        venue,
        dynamicPricing,
        dynamicPricingPerPerson,
        defaultPerPersonPrice
      }),
    [calendarBookingMode, venue, dynamicPricing, dynamicPricingPerPerson, defaultPerPersonPrice]
  );
  const selectedDateHasRealDiscount = useMemo(
    () =>
      selectedCalendarDateHasDiscount({
        bookingMode: calendarBookingMode,
        selectedDateYmd: selectedDate,
        venue,
        dynamicPricing,
        dynamicPricingPerPerson,
        defaultPerPersonPrice
      }),
    [
      calendarBookingMode,
      selectedDate,
      venue,
      dynamicPricing,
      dynamicPricingPerPerson,
      defaultPerPersonPrice
    ]
  );
  const selectedDateCoverageForMode = useMemo(() => {
    if (!selectedDate) return null;
    const [y, m, d] = String(selectedDate).split('-').map(Number);
    if (!Number.isFinite(y)) return null;
    return getDynamicPricingCoverageForDate(new Date(y, m - 1, d), pricingRulesForCoverage);
  }, [selectedDate, pricingRulesForCoverage]);

  // Minimum end time: start + venue min hours for this booking mode (aligned with BookingForm)
  const calculateMinEndTime = useCallback((st) => {
    if (!st) return null;

    const [hours, minutes] = st.split(':').map(Number);
    const startMinutes = hours * 60 + minutes;
    const minEndMinutes = startMinutes + Math.round(minHoursForMode * 60);

    if (minEndMinutes >= 24 * 60) {
      return '23:59';
    }

    const minEndH = Math.floor(minEndMinutes / 60);
    const minMins = minEndMinutes % 60;
    return `${minEndH.toString().padStart(2, '0')}:${minMins.toString().padStart(2, '0')}`;
  }, [minHoursForMode]);

  const selectedDateYmd = useMemo(() => {
    if (typeof selectedDate === 'string') return selectedDate.split('T')[0];
    if (selectedDate instanceof Date && !isNaN(selectedDate.getTime())) {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: venueTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(selectedDate);
    }
    return null;
  }, [selectedDate, venueTimezone]);

  /** Local calendar Date for the selected civil day (no UTC midnight / setUTCHours — fixes wrong weekday & blocks in BST). */
  const date = useMemo(() => {
    if (!selectedDateYmd || !/^\d{4}-\d{2}-\d{2}$/.test(selectedDateYmd)) return null;
    const [y, m, d] = selectedDateYmd.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [selectedDateYmd]);

  // Fetch blocks from unified availability API (mode=blocks): calendar + manual + capacity-exhausted
  useEffect(() => {
    const fetchBlocks = async () => {
      const venueId = venue?.id ? String(venue.id) : null;
      const spaceId = selectedSpaceId && !selectedSpaceId.includes('{') ? selectedSpaceId : null;
      if (!venueId || !spaceId || !selectedDateYmd || !/^\d{4}-\d{2}-\d{2}$/.test(selectedDateYmd)) {
        setBlocks([]);
        return;
      }

      setLoadingBusyTimes(true);
      try {
        const apiBookingMode = bookingType === 'full-venue' ? 'exclusive' : 'per_person';
        const rawBlocks = await venueDashboardApiService.getSpaceAvailabilityBlocks(
          venueId,
          spaceId,
          selectedDateYmd,
          apiBookingMode
        );
        setBlocks(rawBlocks || []);
      } catch {
        setBlocks([]);
      } finally {
        setLoadingBusyTimes(false);
      }
    };

    fetchBlocks();
  }, [venue?.id, selectedSpaceId, selectedDateYmd, bookingType]);
  const pastTimes = useMemo(
    () => getPastTimesForDate(date, venueTimezone, selectedDateYmd || undefined),
    [date, venueTimezone, selectedDateYmd]
  );
  const effectiveOperatingHours = useMemo(
    () => mergeOperatingHoursForBookingMode(bookingType, operatingHours, operatingHoursPerPerson),
    [bookingType, operatingHours, operatingHoursPerPerson]
  );
  const ohBlockOpts = useMemo(
    () => ({ timezone: venueTimezone, dateYmd: selectedDateYmd || undefined }),
    [venueTimezone, selectedDateYmd]
  );
  const operatingHoursBlocked = useMemo(
    () => getBlockedTimesFromOperatingHoursAndHolidays(date, effectiveOperatingHours, holidays, ohBlockOpts),
    [date, effectiveOperatingHours, holidays, ohBlockOpts]
  );
  /** Closing time slot (e.g. 16:00) allowed as end time — only for end picker / blockedEndTimes merge */
  const operatingHoursBlockedEnd = useMemo(
    () => getBlockedTimesFromOperatingHoursAndHolidays(date, effectiveOperatingHours, holidays, {
      inclusiveClosingSlot: true,
      ...ohBlockOpts
    }),
    [date, effectiveOperatingHours, holidays, ohBlockOpts]
  );
  const blockedTimes = useMemo(() => {
    if (!date) return [];
    return buildMergedBlockedStartTimes({
      blocks,
      bookingType,
      date,
      venueTimezone,
      selectedDateYmd: selectedDateYmd || undefined,
      operatingHoursBlocked,
      pastTimes,
      minHours: minHoursForMode,
      operatingHoursBlockedEnd
    });
  }, [blocks, bookingType, date, pastTimes, operatingHoursBlocked, operatingHoursBlockedEnd, venueTimezone, selectedDateYmd, minHoursForMode]);

  // Prefetch blocks for the week around the selected date (not the whole calendar month)
  useEffect(() => {
    const venueId = venue?.id ? String(venue.id) : null;
    const spaceId = selectedSpaceId && !selectedSpaceId.includes('{') ? selectedSpaceId : null;
    if (!venueId || !spaceId) {
      setFullyUnavailableDatesYmd(new Set());
      return;
    }
    let cancelled = false;
    const todayVenueYmd = getYmdInTimezone(new Date(), venueTimezone);

    const buildWeekDayJobs = () => {
      if (!selectedDateYmd || !/^\d{4}-\d{2}-\d{2}$/.test(selectedDateYmd)) {
        return [];
      }
      const [y, m, d] = selectedDateYmd.split('-').map(Number);
      const anchor = new Date(y, m - 1, d);
      const weekStart = new Date(anchor);
      weekStart.setDate(anchor.getDate() - anchor.getDay());
      const jobs = [];
      for (let i = 0; i < 7; i += 1) {
        const dateObj = new Date(weekStart);
        dateObj.setDate(weekStart.getDate() + i);
        const ymd = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
        if (ymd < todayVenueYmd) continue;
        jobs.push({ dateObj, ymd });
      }
      return jobs;
    };

    (async () => {
      const dayJobs = buildWeekDayJobs();

      const initialUnavailable = new Set();
      const toFetch = [];
      for (const job of dayJobs) {
        const cacheKey = `${availabilityDayCachePrefix}|${job.ymd}`;
        if (dayAvailabilityCacheRef.current.has(cacheKey)) {
          if (dayAvailabilityCacheRef.current.get(cacheKey)) {
            initialUnavailable.add(job.ymd);
          }
        } else {
          toFetch.push(job);
        }
      }
      // Cached days apply immediately; uncached days fill in as each request resolves.
      setFullyUnavailableDatesYmd(new Set(initialUnavailable));

      const minHoursExclusive = bookingRules?.min_hours_exclusive ?? bookingRules?.min_total_hours ?? 2;
      const minHoursPerPerson = bookingRules?.min_hours_per_person ?? 2;

      const evaluateDay = async ({ dateObj, ymd }) => {
        const cacheKey = `${availabilityDayCachePrefix}|${ymd}`;
        try {
          const exBlocks = await venueDashboardApiService.getSpaceAvailabilityBlocks(
            venueId,
            spaceId,
            ymd,
            'exclusive'
          );
          const ppBlocks = perPersonEnabled
            ? await venueDashboardApiService.getSpaceAvailabilityBlocks(
              venueId,
              spaceId,
              ymd,
              'per_person'
            )
            : [];
          if (cancelled) return;
          const pastTimesForDay = getPastTimesForDate(dateObj, venueTimezone, ymd);
          const ohEx = getBlockedTimesFromOperatingHoursAndHolidays(
            dateObj,
            mergeOperatingHoursForBookingMode('full-venue', operatingHours, operatingHoursPerPerson),
            holidays,
            { timezone: venueTimezone, dateYmd: ymd }
          );
          const ohPp = getBlockedTimesFromOperatingHoursAndHolidays(
            dateObj,
            mergeOperatingHoursForBookingMode('table', operatingHours, operatingHoursPerPerson),
            holidays,
            { timezone: venueTimezone, dateYmd: ymd }
          );
          const ohExEnd = getBlockedTimesFromOperatingHoursAndHolidays(
            dateObj,
            mergeOperatingHoursForBookingMode('full-venue', operatingHours, operatingHoursPerPerson),
            holidays,
            { inclusiveClosingSlot: true, timezone: venueTimezone, dateYmd: ymd }
          );
          const ohPpEnd = getBlockedTimesFromOperatingHoursAndHolidays(
            dateObj,
            mergeOperatingHoursForBookingMode('table', operatingHours, operatingHoursPerPerson),
            holidays,
            { inclusiveClosingSlot: true, timezone: venueTimezone, dateYmd: ymd }
          );
          const fullyUnavailable = isDateFullyUnavailableForBothModes({
            blocksExclusive: exBlocks || [],
            blocksPerPerson: ppBlocks || [],
            date: dateObj,
            venueTimezone,
            selectedDateYmd: ymd,
            operatingHoursBlockedExclusive: ohEx,
            operatingHoursBlockedPerPerson: ohPp,
            operatingHoursBlockedEndExclusive: ohExEnd,
            operatingHoursBlockedEndPerPerson: ohPpEnd,
            pastTimes: pastTimesForDay,
            minHoursExclusive,
            minHoursPerPerson,
            perPersonEnabled
          });
          if (!cancelled) {
            dayAvailabilityCacheRef.current.set(cacheKey, fullyUnavailable);
            if (fullyUnavailable) {
              setFullyUnavailableDatesYmd((prev) => {
                const next = new Set(prev);
                next.add(ymd);
                return next;
              });
            }
          }
        } catch {
          /* keep day selectable; do not cache so a later visit can retry */
        }
      };

      // Only uncached days hit the network (browser limits concurrent connections).
      await Promise.all(toFetch.map((job) => evaluateDay(job)));
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedDateYmd,
    selectedSpaceId,
    venue?.id,
    venueTimezone,
    operatingHours,
    operatingHoursPerPerson,
    holidays,
    bookingRules,
    availabilityDayCachePrefix,
    perPersonEnabled
  ]);

  const blockedEndTimes = useMemo(() => {
    if (!date || !startTime) return [];
    return buildMergedBlockedEndTimesForStart({
      startTime,
      blocks,
      bookingType,
      date,
      venueTimezone,
      selectedDateYmd: selectedDateYmd || undefined,
      operatingHoursBlockedEnd,
      pastTimes
    });
  }, [blocks, bookingType, date, startTime, operatingHoursBlockedEnd, pastTimes, venueTimezone, selectedDateYmd]);

  const hasValidEndSlot = useMemo(() => {
    if (!startTime || loadingBusyTimes) return true;
    const minEndStr = calculateMinEndTime(startTime);
    if (!minEndStr) return true;
    return pickFirstValidEndAfterStart(startTime, blockedEndTimes, minEndStr) != null;
  }, [startTime, blockedEndTimes, loadingBusyTimes, calculateMinEndTime]);

  useEffect(() => {
    const raw = spacesProp ?? venue?.spaces ?? [];
    const normalized = normalizeVenueSpacesForDisplay(raw);
    if (normalized.length > 0) {
      setSelectedSpaceId(normalized[0].id || '');
    }
  }, [venue, spacesProp]);

  // Debounce headcount so typing doesn't refetch (and steal focus) on every keystroke
  useEffect(() => {
    const parsed = typeof tableCount === 'number' ? tableCount : parseInt(tableCount, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return;
    const timer = setTimeout(() => setDebouncedTableCount(parsed), 400);
    return () => clearTimeout(timer);
  }, [tableCount]);

  // Fetch space availability + pricing from availability-preview API
  // Space ID must come from GET /venues/{venue_id}/spaces (never a placeholder)
  useEffect(() => {
    const fetchAvailability = async () => {
      const venueId = venue?.id ? String(venue.id) : null;
      const spaceId = selectedSpaceId && !selectedSpaceId.includes('{') ? selectedSpaceId : null;
      if (!venueId || !spaceId || !selectedDateYmd || !startTime || !endTime) {
        setAvailabilityPreview(null);
        return;
      }
      setLoadingAvailability(true);
      try {
        const bookingMode = bookingType === 'full-venue' ? 'exclusive' : 'per_person';
        const params = {
          date: selectedDateYmd,
          startTime: startTime.length >= 5 ? startTime : `${startTime}:00`,
          endTime: endTime.length >= 5 ? endTime : `${endTime}:00`,
          bookingMode,
          headcount: bookingMode === 'per_person' ? debouncedTableCount : undefined,
          timezone: venueTimezone
        };
        const data = await venueDashboardApiService.getSpaceAvailabilityPreview(
          venueId,
          spaceId,
          params
        );
        setAvailabilityPreview(data);
      } catch {
        setAvailabilityPreview(null);
      } finally {
        setLoadingAvailability(false);
      }
    };
    fetchAvailability();
  }, [venue?.id, venueTimezone, selectedSpaceId, selectedDateYmd, startTime, endTime, bookingType, debouncedTableCount]);

  const spaces = useMemo(() => {
    const raw = spacesProp ?? venue?.spaces ?? [];
    const venueSeatingCapacity =
      venue?.maxCapacity ?? venue?.max_capacity ?? venue?.capacity ?? null;
    return normalizeVenueSpacesForDisplay(raw).map((space) => ({
      id: space.id,
      name: space.name,
      capacityTotal: space.capacityTotal ?? venueSeatingCapacity ?? null,
      tablesAvailable: space.capacityTotal ?? venueSeatingCapacity ?? 20
    }));
  }, [spacesProp, venue?.spaces, venue?.maxCapacity, venue?.max_capacity, venue?.capacity]);

  const selectedSpace = spaces.find((space) => space.id === selectedSpaceId);
  const duration = calculateDuration(startTime, endTime);
  // Use API remaining_capacity for per_person, capacity_total for full venue
  const tablesAvailable =
    availabilityPreview?.remaining_capacity ??
    availabilityPreview?.capacity_total ??
    selectedSpace?.capacityTotal ??
    selectedSpace?.tablesAvailable ??
    venue?.maxCapacity ??
    venue?.max_capacity ??
    20;

  const parsedTableCount =
    typeof tableCount === 'number' ? tableCount : parseInt(String(tableCount), 10);
  const hasValidAttendees = Number.isFinite(parsedTableCount) && parsedTableCount >= 1;

  // Cap tableCount when capacity drops (don't overwrite empty/in-progress input)
  useEffect(() => {
    if (!hasValidAttendees) return;
    if (tablesAvailable > 0 && parsedTableCount > tablesAvailable) {
      setTableCount(tablesAvailable);
    }
  }, [tablesAvailable, hasValidAttendees, parsedTableCount]);

  const rawBlockingReasons = availabilityPreview?.blocking_reasons || [];
  const apiSaysAvailable = availabilityPreview?.is_available === true;
  const clientSaysOutsideHours = operatingHoursBlocked.includes(startTime);

  const mapApiReasonToStandard = (r) => {
    const s = String(r).toLowerCase();
    if (s === 'outside_operating_hours') return 'Outside operating hours';
    if (s === 'manual_or_calendar_block') return 'No start time available';
    if (s.includes('overlapping')) return 'No start time available';
    return 'No start time available';
  };

  const blockingReasons = [];
  const pushBlocking = (msg) => {
    if (msg && !blockingReasons.includes(msg)) blockingReasons.push(msg);
  };

  for (const r of rawBlockingReasons) {
    const s = String(r);
    if (s === 'outside_operating_hours') {
      if (!apiSaysAvailable && clientSaysOutsideHours) pushBlocking('Outside operating hours');
      continue;
    }
    pushBlocking(mapApiReasonToStandard(r));
  }
  if (clientSaysOutsideHours && !blockingReasons.some((msg) => String(msg).toLowerCase().includes('outside'))) {
    pushBlocking('Outside operating hours');
  }
  if (!loadingBusyTimes && startTime && !hasValidEndSlot) {
    pushBlocking('No end time available');
  }
  // Do not treat missing preview as "available" — client/API blocking reasons control the button state
  const isAvailable = blockingReasons.length === 0;

  const nextAvailableStart = getNextAvailableStartTime(blockedTimes, startTime);
  const hasNoAvailableBlock = bookingType === 'full-venue' && getNextAvailableStartTime(blockedTimes, '00:00') === null;

  // getActualPrice: returns { rate, hasApplicableRule } - dynamic pricing when rule applies for date/time
  const getActualPrice = (date, st, et) => {
    const baseRate = venue?.hourly_rate || venue?.hourlyRate || venue?.price || 0;
    if (!venue) return { rate: 0, hasApplicableRule: false };
    if (!selectedDateYmd || !dynamicPricing?.length) return { rate: baseRate, hasApplicableRule: false };
    const ymd = selectedDateYmd;
    if (!ymd) return { rate: baseRate, hasApplicableRule: false };
    const applicableRules = dynamicPricing.filter((rule) => {
      if (!rule.start_date || !rule.end_date) return false;
      const [sY, sM, sD] = rule.start_date.split('-').map(Number);
      const [eY, eM, eD] = rule.end_date.split('-').map(Number);
      const [dY, dM, dD] = ymd.split('-').map(Number);
      const startDate = new Date(sY, sM - 1, sD);
      const endDate = new Date(eY, eM - 1, eD);
      const selectedDateObj = new Date(dY, dM - 1, dD);
      return selectedDateObj >= startDate && selectedDateObj <= endDate;
    });
    if (applicableRules.length === 0) return { rate: baseRate, hasApplicableRule: false };
    const { rate, matchedDynamicRule } = resolveFullVenueDynamicHourlyRate(
      applicableRules,
      st,
      et,
      baseRate
    );
    return { rate, hasApplicableRule: matchedDynamicRule };
  };

  const baseHourlyRate = venue?.hourly_rate || venue?.hourlyRate || venue?.price || 0;
  // Priority 1: Dynamic pricing (date/time specific rate) - when rule applies
  const { rate: dynamicPricingRate, hasApplicableRule: hasDynamicRate } = getActualPrice(selectedDate, startTime, endTime);
  // Priority 2: Flat pricing (when duration >= min_hours; use highest matching threshold)
  const activeFlatRules = (flatPricing || []).filter(r => r.is_active !== false);
  const matchingFlatRule = activeFlatRules
    .filter(r => duration >= (r.min_hours || 0))
    .sort((a, b) => (b.min_hours || 0) - (a.min_hours || 0))[0];
  const flatPriceTotal = matchingFlatRule?.flat_price != null ? Number(matchingFlatRule.flat_price) : null;
  // Priority 3: Default pricing (venue hourly rate)
  // Effective hourly: dynamic > default (flat applies to total, not hourly)
  const effectiveHourlyRate = hasDynamicRate ? dynamicPricingRate : baseHourlyRate;
  const hourlyRate = effectiveHourlyRate;
  // Estimated total: dynamic (hourly*duration) > flat (flat_price) > default (hourly*duration)
  const usingFlatPricing = bookingType === 'full-venue' && !hasDynamicRate && flatPriceTotal != null;
  const perPersonRate = resolvePerPersonRateForBooking({
      availabilityPreview,
      date: selectedDate,
      startTime,
      endTime,
      dynamicPricingPerPerson,
      defaultPerPersonPrice,
      defaultHourlyPrice: hourlyRate
    });
  const estimatedTotal = (() => {
    if (bookingType === 'full-venue') {
      if (hasDynamicRate) return hourlyRate * duration;
      if (flatPriceTotal != null) return flatPriceTotal;
      return hourlyRate * duration;
    }
    return (perPersonRate ?? 0) * (hasValidAttendees ? parsedTableCount : 0) * duration;
  })();

  const lastAppliedDefaultKeyRef = useRef(null);
  useEffect(() => {
    lastAppliedDefaultKeyRef.current = null;
  }, [selectedDateYmd, selectedSpaceId, bookingType]);

  // After date (or space / booking mode) changes, default to first available start once blocks have loaded.
  useLayoutEffect(() => {
    if (!selectedDateYmd || loadingBusyTimes) return;
    const key = `${selectedDateYmd}|${selectedSpaceId}|${bookingType}`;
    if (lastAppliedDefaultKeyRef.current === key) return;
    const first = getNextAvailableStartTime(blockedTimes, '00:00');
    if (!first) {
      lastAppliedDefaultKeyRef.current = key;
      return;
    }
    if (startTime === first) {
      lastAppliedDefaultKeyRef.current = key;
      return;
    }
    onStartTimeChange({ target: { value: first } });
    lastAppliedDefaultKeyRef.current = key;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onStartTimeChange from parent
  }, [selectedDateYmd, loadingBusyTimes, selectedSpaceId, bookingType, blockedTimes, startTime]);

  // When start time is blocked for this date, auto-move to next available (full-venue and per-person).
  // Skip while blocks are loading to avoid flickering.
  useEffect(() => {
    if (!nextAvailableStart || loadingBusyTimes) return;
    if (!blockedTimes.includes(startTime)) return;
    const newEndTime = calculateMinEndTime(nextAvailableStart);
    if (newEndTime) {
      onStartTimeChange({ target: { value: nextAvailableStart } });
      onEndTimeChange({ target: { value: newEndTime } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onStartTimeChange/onEndTimeChange are stable
  }, [bookingType, startTime, blockedTimes, nextAvailableStart, loadingBusyTimes]);

  // One pass: min duration + not blocked (avoids two updates e.g. first unblocked 15:30 then bump to 16:00).
  // useLayoutEffect so the first paint after blocks load already shows the corrected end time.
  useLayoutEffect(() => {
    if (!startTime || !endTime || loadingBusyTimes) return;
    const minEnd = calculateMinEndTime(startTime);
    if (!minEnd) return;
    if (endTime >= minEnd && !blockedEndTimes.includes(endTime)) return;
    const firstValid = pickFirstValidEndAfterStart(startTime, blockedEndTimes, minEnd);
    if (firstValid != null && firstValid !== endTime) {
      onEndTimeChange({ target: { value: firstValid } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onEndTimeChange from parent may be inline
  }, [startTime, endTime, blockedEndTimes, loadingBusyTimes, calculateMinEndTime]);

  const handleStartTimeChange = (time) => {
    onStartTimeChange({ target: { value: time } });
  };
  const showDiscountBanner = !hidePrice && selectedDate && venueHasAnyDiscountedDay;
  return (
    <div className={`booking-sidebar${sidebarDataLoading ? ' booking-sidebar--loading-data' : ''}`}>
      {sidebarDataLoading ? (
        <div className="booking-sidebar-loading-overlay" aria-live="polite">
          <div className="loading-spinner" />
          <span>Loading booking options...</span>
        </div>
      ) : null}
      <div className="booking-widget">
        {hidePrice ? (
          <div className="booking-info-banner" role="note">
            <img
              className="booking-info-banner-icon"
              src="/manual-booking/icons/contact-call-orange.png"
              alt=""
              aria-hidden="true"
              width="20"
              height="20"
            />
            Contact venue for pricing
          </div>
        ) : null}
        <div className="booking-field">
          <label className="booking-field-label">
            Space <span className="required">*</span>
          </label>
          <SpaceSelect
            spaces={spaces}
            value={selectedSpaceId}
            onChange={setSelectedSpaceId}
          />
        </div>

        {perPersonEnabled ? (
          <div className="booking-field">
            <label className="booking-field-label">
              Type of booking <span className="required">*</span>
            </label>
            <div className="booking-radio-group">
              <label className="booking-radio-option">
                <input
                  type="radio"
                  name="bookingType"
                  value="table"
                  checked={bookingType === 'table'}
                  onChange={(event) => setBookingType(event.target.value)}
                />
                <span className="booking-radio-label-stack">
                  <span className="booking-radio-title">Per person</span>
                  <span className="booking-radio-sub">During operating hours</span>
                </span>
                <span className="booking-radio-custom" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </label>
              <label className="booking-radio-option">
                <input
                  type="radio"
                  name="bookingType"
                  value="full-venue"
                  checked={bookingType === 'full-venue'}
                  onChange={(event) => setBookingType(event.target.value)}
                />
                <span className="booking-radio-label-stack">
                  <span className="booking-radio-title">Full venue hire</span>
                  <span className="booking-radio-sub">During and outside operating hours</span>
                </span>
                <span className="booking-radio-custom" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </label>
            </div>
          </div>
        ) : (
          <div className="booking-field">
            <label className="booking-field-label">Type of booking</label>
            <p className="booking-helper-text">Full venue hire</p>
          </div>
        )}

        <h3>Date and time <span className="required">*</span></h3>

        <div className={`booking-datetime-block${showDiscountBanner ? ' is-discounted' : ''}${blockingReasons.length > 0 ? ' is-unavailable' : ''}`}>
          {showDiscountBanner && (
            <div className="booking-discount-banner">
              <OffersIcon />
              <p>Discounted rates for select dates</p>
            </div>
          )}
          {blockingReasons.length > 0 && (
            <div className="booking-discount-banner booking-availability-error" role="alert">
              <InfoCircleIcon aria-hidden="true" />
              <p>{blockingReasons.join(', ')}</p>
            </div>
          )}

          <div className="custom-calendar-container">
          <PriceCalendar
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            venue={venue}
            dynamicPricing={dynamicPricing}
            dynamicPricingPerPerson={dynamicPricingPerPerson}
            defaultPerPersonPrice={defaultPerPersonPrice}
            flatPricing={flatPricing}
            minBookingHours={minHoursForMode}
            bookingMode={calendarBookingMode}
            showDiscountedRatesBanner={false}
            hidePrice={hidePrice}
            fullyUnavailableDatesYmd={fullyUnavailableDatesYmd}
            onVisibleMonthChange={setCalendarVisibleMonth}
            matchInputWidth
          />
          
          {/* Dynamic Pricing Message - Below Date Picker (hidden when venue hides price) */}
          {!hidePrice && selectedDateCoverageForMode && selectedDate && selectedDateHasRealDiscount && (
            <div style={{
              marginTop: '4px',
              width: '100%'
            }}>
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                fontWeight: '500',
                lineHeight: '16px',
                color: '#5a9327',
                margin: 0,
                textAlign: 'left'
              }}>
                {selectedDateCoverageForMode === 'all_hours' 
                  ? 'Discounted rates for all hours'
                  : 'Discounted rates for select hours'
                }
              </p>
            </div>
          )}
        </div>
        
        <TimeRangePicker
          startTime={startTime}
          endTime={endTime}
          onStartChange={handleStartTimeChange}
          onEndChange={(time) => onEndTimeChange({ target: { value: time } })}
          startPlaceholder="Select start time"
          endPlaceholder="Select end time"
          blockedTimes={blockedTimes}
          blockedEndTimes={blockedEndTimes}
          minEndTime={calculateMinEndTime(startTime)}
          endDisabled={!startTime}
        />
        </div>

        <p className="booking-helper-text">
          Minimum {minHoursForMode === 1 ? '1 hour' : `${minHoursForMode} hours`}
        </p>

        {bookingType === 'table' && (
          <div className="booking-field">
            <label className="booking-field-label">
              No. of attendees <span className="required">*</span>
            </label>
            <div className="booking-select-wrapper mb-guest-stepper">
              <input
                type="number"
                className="booking-select booking-number-input mb-input"
                min={1}
                max={Math.max(1, tablesAvailable)}
                step={1}
                value={tableCount}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setTableCount('');
                    return;
                  }
                  const parsed = parseInt(raw, 10);
                  if (!Number.isFinite(parsed)) {
                    setTableCount('');
                    return;
                  }
                  // Allow free typing; clamp to capacity on blur
                  setTableCount(parsed);
                }}
                onBlur={() => {
                  if (tableCount === '' || !Number.isFinite(Number(tableCount)) || Number(tableCount) < 1) {
                    return;
                  }
                  const max = Math.max(1, tablesAvailable);
                  if (Number(tableCount) > max) {
                    setTableCount(max);
                  }
                }}
                aria-label="Number of attendees"
              />
              <div className="mb-guest-stepper-actions">
                <button
                  type="button"
                  className="mb-guest-stepper-btn"
                  aria-label="Decrease attendees"
                  disabled={!tableCount || Number(tableCount) <= 1}
                  onClick={() => setTableCount(Math.max(1, Number(tableCount) - 1))}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="mb-guest-stepper-btn"
                  aria-label="Increase attendees"
                  disabled={Number(tableCount) >= Math.max(1, tablesAvailable)}
                  onClick={() =>
                    setTableCount(Math.min(Math.max(1, tablesAvailable), (Number(tableCount) || 0) + 1))
                  }
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </div>
            <p className="booking-helper-text">
              {loadingAvailability ? 'Checking availability...' : `${tablesAvailable} capacity remaining for the selected date and time slot`}
            </p>
          </div>
        )}
        
        {/*<div className="flexibility-option">*/}
        {/*  <input*/}
        {/*    type="checkbox"*/}
        {/*    id="flexible"*/}
        {/*    checked={isFlexible}*/}
        {/*    onChange={onFlexibleChange}*/}
        {/*  />*/}
        {/*  <label htmlFor="flexible">I'm flexible on dates and time</label>*/}
        {/*</div>*/}
        
        <div className="booking-checkout">
        {hidePrice ? null : (
          <>
            <div className="estimated-total">
              <span>Space Fee</span>
              <span className="total-price">£{Number(estimatedTotal).toFixed(2)}</span>
            </div>
            <div className="cost-breakdown">
              <div className="cost-line">(
                {bookingType === 'full-venue' ? (
                  usingFlatPricing ? (
                    <span>Flat rate ({matchingFlatRule?.min_hours}+ hours)</span>
                  ) : (
                    <span>£{Number(hourlyRate).toFixed(2)}/hour × {duration.toFixed(1)} hours</span>
                  )
                ) : perPersonRate != null ? (
                  <span>£{Number(perPersonRate).toFixed(2)}/person/hr × {hasValidAttendees ? parsedTableCount : '—'} people × {duration.toFixed(1)} hrs</span>
                ) : (
                  <span>Per-person rate not set</span>
                )})
              </div>
            </div>
          </>
        )}
        <div className="booking-checkout-cta">
        <button
          type="button"
          className="book-button"
          disabled={
            loadingAvailability ||
            !isAvailable ||
            hasNoAvailableBlock ||
            (bookingType === 'table' && !hasValidAttendees)
          }
          onClick={() =>
            onBookNow({
              date: selectedDate,
              startTime,
              endTime,
              spaceId: selectedSpaceId,
              bookingType,
              tableCount: hasValidAttendees ? parsedTableCount : tableCount
            })
          }
        >
          {bookButtonLabel}
        </button>
        <p className="booking-disclaimer">You won't be charged yet.</p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default VenueBookingSidebar;

